import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  Bell,
  User,
  LogOut,
  ChevronDown,
  Moon,
  Sun,
  Home,
  HelpCircle,
  Palette
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu';
import { useTheme } from '../theme/ThemeProvider';
import { brandConfig } from '../config';
import { useAuthContext } from '../auth/useAuthContext';
import { PATH_PAGE, PATH_HOME, PATH_DEV } from '../routes/paths';
import useResponsive from '../hooks/useResponsive';
import DashboardNav from './nav/DashboardNav';
import { CheckEnvironment, SupportedEnvironments } from '../config-global';
import { ContactUsPopover } from '../components/contact-us';
import NetPromoterScore from 'src/components/net-promoter-scale';
import { Roles } from '../utils/owner-portal/userConstants';
import { useGetRTKQOwnerProfileQuery } from '../redux/rtkQuery/apiSlice';
import { safeGetItem, safeRemoveItem, safeSetItem } from '../utils/safeStorage';

const TimeshareAnnouncementModal = lazy(
  () =>
    import(
      /* webpackChunkName: "timeshare-announcement-modal" */
      '../sections/timeshare/announcement/TimeshareAnnouncementModal'
    )
);

interface DashboardLayoutProps {
  showNotifications?: boolean;
}

const MENU_LOCKED_STORAGE_KEY = 'dashboard-menu-locked';

function getInitialMenuLocked(): boolean {
  if (typeof window === 'undefined') return false;
  return safeGetItem('session', MENU_LOCKED_STORAGE_KEY) === 'true';
}

export default function DashboardLayout({ showNotifications = true }: DashboardLayoutProps) {
  const [menuLocked, setMenuLocked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactPopoverOpen, setContactPopoverOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const isDesktop = useResponsive('up', 'lg');

  const location = useLocation();
  const { resolvedMode, toggleMode } = useTheme();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const isOwner = user?.role === Roles.User;
  const { data: ownerProfile } = useGetRTKQOwnerProfileQuery(undefined, { skip: !isOwner });
  // Salesforce: email sets nps_last_send_date; show until a submit on/after that send.
  // Local ignoreNPS dismiss (close/X) is handled inside NetPromoterScore — not opt-out.
  const showNps =
    isOwner &&
    ownerProfile?.npsOptOut === false &&
    ownerProfile.npsLastSendDate != null &&
    (ownerProfile.npsLastSubmitDate == null ||
      ownerProfile.npsLastSubmitDate < ownerProfile.npsLastSendDate);

  // Restore locked state from sessionStorage on mount and on every navigation
  // (each route branch has its own DashboardLayout instance, so we remount and need to re-read)
  useEffect(() => {
    const stored = getInitialMenuLocked();
    if (stored) {
      setMenuLocked(true);
      setMenuOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    navigate(PATH_PAGE.logout);
  };

  const handleMenuButtonClick = () => {
    if (isDesktop) {
      setMenuLocked((prev) => {
        const next = !prev;
        setMenuOpen(next);
        if (next) {
          safeSetItem('session', MENU_LOCKED_STORAGE_KEY, 'true');
        } else {
          safeRemoveItem('session', MENU_LOCKED_STORAGE_KEY);
        }
        return next;
      });
    } else {
      setMenuOpen((prev) => !prev);
    }
  };

  const handleAsideMouseEnter = () => {
    if (isDesktop && !menuLocked) setMenuOpen(true);
  };

  const handleAsideMouseLeave = () => {
    if (isDesktop && !menuLocked) setMenuOpen(false);
  };

  return (
    <div className="flex h-dvh min-h-dvh flex-col overflow-hidden bg-background">
      {brandConfig.postLoginModal?.length ? (
        <Suspense fallback={null}>
          <TimeshareAnnouncementModal />
        </Suspense>
      ) : null}
      {/* Header - menu button inside so it's not covered; centered over collapsed sidebar */}
      <header className="relative flex h-16 shrink-0 items-center justify-between gap-2 overflow-visible border-b bg-card px-4 lg:pl-16">
        <Button
          variant="ghost"
          size="icon"
          className="z-10 shrink-0 lg:absolute lg:left-[1.75rem] lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2"
          onClick={handleMenuButtonClick}
          aria-label={
            isDesktop
              ? menuLocked
                ? 'Unlock menu (collapse on mouse leave)'
                : 'Lock menu open'
              : menuOpen
              ? 'Close menu'
              : 'Open menu'
          }
        >
          <Menu
            className={cn(
              'h-5 w-5 shrink-0',
              isDesktop && (menuLocked ? 'text-foreground' : 'text-muted-foreground')
            )}
          />
        </Button>
        <div className="flex min-w-0 flex-1 items-center">
          <img
            src={resolvedMode === 'dark' ? brandConfig.iconUrlDark : brandConfig.iconUrl}
            alt={brandConfig.name}
            className="h-8 w-8 shrink-0 object-contain object-left lg:hidden"
          />
          <img
            src={resolvedMode === 'dark' ? brandConfig.logoUrlDark : brandConfig.logoUrl}
            alt={brandConfig.name}
            className="hidden h-8 w-auto max-h-8 object-contain object-left lg:block"
          />
        </div>

        <div className="flex flex-1 items-center justify-end sm:gap-2">
          <ContactUsPopover
            trigger={
              <Button variant="ghost" className="text-sm text-muted-foreground">
                Contact Us
              </Button>
            }
          />

          {/* Theme toggle */}
          <Button variant="ghost" size="icon" onClick={toggleMode}>
            {resolvedMode === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Notifications */}
          {showNotifications && (
            <Button variant="ghost" size="icon" onClick={() => navigate('/user/notifications')}>
              <Bell className="h-5 w-5" />
            </Button>
          )}

          <DropdownMenu
            modal={false}
            open={userMenuOpen}
            onOpenChange={(open) => {
              setUserMenuOpen(open);
              if (!open) setContactPopoverOpen(false);
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <User className="h-4 w-4" />
                </div>
                <span className="hidden md:inline-block">{user?.displayName || 'User'}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={cn(
                'w-56 max-lg:w-[min(14rem,calc(100vw-2rem))] max-lg:max-w-[calc(100vw-2rem)]',
                'overflow-visible'
              )}
              onInteractOutside={(e) => {
                if (contactPopoverOpen) e.preventDefault();
              }}
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.displayName || 'User'}</p>
                  <p className="max-w-full truncate text-xs leading-none text-muted-foreground" title={user?.email || undefined}>
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(PATH_HOME.root)}>
                <Home className="mr-2 h-4 w-4" />
                Home
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(PATH_HOME.user.account)}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(PATH_PAGE.faqs)}>
                <HelpCircle className="mr-2 h-4 w-4" />
                FAQs
              </DropdownMenuItem>
              {CheckEnvironment(SupportedEnvironments.Dev) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(PATH_DEV.brand)}>
                    <Palette className="mr-2 h-4 w-4" />
                    Brand Selector
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Body: drawer + main (desktop) or just main (mobile when drawer closed) */}
      <div className="flex min-h-0 flex-1">
        {/* Desktop, unlocked: rail (always w-14) + hover overlay (fixed, covers content, no shift) */}
        {isDesktop && !menuLocked && (
          <>
            {/* Collapsed rail - in flow so main content doesn't shift */}
            <aside
              className="hidden lg:flex shrink-0 flex-col border-r bg-card w-14 transition-[width] duration-300 ease-in-out"
              onMouseEnter={handleAsideMouseEnter}
              aria-hidden={menuOpen}
            >
              <DashboardNav collapsed onClose={() => setMenuOpen(false)} />
            </aside>
            {/* Expanded menu as fixed overlay on hover - covers content, higher z-index */}
            {menuOpen && (
              <aside
                className="hidden lg:flex fixed left-0 top-16 bottom-0 z-50 w-64 shrink-0 flex-col border-r bg-card shadow-lg transition-opacity duration-200"
                onMouseLeave={handleAsideMouseLeave}
              >
                <DashboardNav collapsed={false} onClose={() => setMenuOpen(false)} />
              </aside>
            )}
          </>
        )}

        {/* Desktop, locked: single sidebar in flow (content shifts when open/closed) */}
        {isDesktop && menuLocked && (
          <aside
            className={cn(
              'hidden lg:flex shrink-0 flex-col border-r bg-card transition-[width] duration-300 ease-in-out',
              menuOpen ? 'w-64' : 'w-14'
            )}
            onMouseEnter={handleAsideMouseEnter}
            onMouseLeave={handleAsideMouseLeave}
          >
            <DashboardNav collapsed={!menuOpen} onClose={() => setMenuOpen(false)} />
          </aside>
        )}

        {/* Mobile: fixed drawer (overlay), no shift */}
        {!isDesktop && (
          <aside
            className={cn(
              'flex shrink-0 flex-col border-r bg-card transition-[transform] duration-300 ease-in-out',
              'fixed left-0 top-16 bottom-0 z-50 w-64',
              menuOpen ? 'translate-x-0' : '-translate-x-full'
            )}
          >
            <DashboardNav collapsed={false} onClose={() => setMenuOpen(false)} />
          </aside>
        )}

        {/* Mobile overlay when drawer open */}
        {menuOpen && !isDesktop && (
          <button
            type="button"
            className="fixed inset-0 top-16 z-40 bg-black/50 lg:hidden cursor-default"
            onClick={() => setMenuOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMenuOpen(false);
            }}
            aria-label="Close menu"
          />
        )}


      {/* Net Promoter Score - owners only, after email send until submit */}
      {showNps && ownerProfile?.npsLastSendDate != null && (
        <NetPromoterScore showOnLoad npsLastSendDate={ownerProfile.npsLastSendDate} />
      )}

        {/* Main content */}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
